import qs from 'qs';

import type { actions } from '@luxuryescapes/contract-flights-charter';

import type {
  CharterReferralRow,
  CreateCharterReferralBody,
  CreateCharterReferralRequestBody,
  CreateCharterReferralResponse,
  DeleteCharterReferralRequestBody,
  DeleteCharterReferralResponse,
  ListCharterReferralParams,
  ListCharterReferralResponse,
  UpdateCharterReferralBody,
  UpdateCharterReferralResponse,
} from '~/types/charterReferral';

import { json_headers, request } from './common';

const BASE_PATH = `${window.configs.API_HOST}/api/flights-charter/v1/fnf-referrals`;

type RequestOptions = {
  signal?: AbortSignal;
};

type ListCharterReferralApiResponse = actions['fnfReferralGetBatch']['responses'][200]['content']['application/json'];

type CreateCharterReferralApiResponse = actions['fnfReferralCreate']['responses'][201]['content']['application/json'];

type UpdateCharterReferralApiResponse = actions['fnfReferralUpdate']['responses'][200]['content']['application/json'];

type CharterReferralApiRow =
  | ListCharterReferralApiResponse['result'][number]
  | CreateCharterReferralApiResponse['result'][number]
  | UpdateCharterReferralApiResponse['result'][number];

function toCharterReferralRow(referral: CharterReferralApiRow, serialNumber = 0): CharterReferralRow {
  return {
    id: referral.fnfReferralId,
    serialNumber,
    fnfName: referral.fnfName,
    fnfEmail: referral.fnfEmail,
    referralName: referral.referralName,
    referralEmail: referral.referralEmail,
    createdAt: referral.createdAt,
  };
}

export async function listCharterReferral(
  params: Partial<ListCharterReferralParams> = {},
  options: RequestOptions = {},
): Promise<ListCharterReferralResponse> {
  const query = qs.stringify(params, { skipNulls: true });
  const url = query ? `${BASE_PATH}?${query}` : BASE_PATH;

  const response: ListCharterReferralApiResponse = await request(url, {
    ...options,
    method: 'GET',
  });

  return {
    status: response.status,
    message: response.message,
    result: response.result.map((referral, index) => toCharterReferralRow(referral, (params.offset ?? 0) + index + 1)),
    pagination: {
      limit: response.pagination.limit,
      offset: response.pagination.offset,
      totalItems: response.pagination.totalItems,
      hasNext: response.pagination.hasNext,
      hasPrevious: response.pagination.hasPrevious,
    },
  };
}

export async function createCharterReferral(body: CreateCharterReferralBody): Promise<CreateCharterReferralResponse> {
  const requestBody: CreateCharterReferralRequestBody = {
    items: [body],
  };

  const response: CreateCharterReferralApiResponse = await request(BASE_PATH, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(requestBody),
  });

  return {
    status: response.status,
    message: response.message,
    result: response.result.map((referral) => toCharterReferralRow(referral)),
  };
}

export async function updateCharterReferral(body: UpdateCharterReferralBody): Promise<UpdateCharterReferralResponse> {
  const requestBody = {
    items: [body],
  };
  const response: UpdateCharterReferralApiResponse = await request(BASE_PATH, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(requestBody),
  });

  return {
    status: response.status,
    message: response.message,
    result: response.result.map((referral) => toCharterReferralRow(referral)),
  };
}

export async function deleteCharterReferral(fnfReferralIds: Array<string>): Promise<DeleteCharterReferralResponse> {
  const requestBody: DeleteCharterReferralRequestBody = {
    items: fnfReferralIds.map((fnfReferralId) => ({
      fnfReferralId,
    })),
  };

  return request(BASE_PATH, {
    method: 'DELETE',
    headers: json_headers,
    body: JSON.stringify(requestBody),
  });
}
